import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Landmark, Link2, Plus, RefreshCw, Search, SearchX, Tags, TriangleAlert, Unplug } from "lucide-react";
import { toast } from "sonner";
import { useDolfinClient } from "@/components/DolfinProvider";
import { CreateLedgerAccountDialog } from "@/components/CreateLedgerAccountDialog";
import { apiErrorMessage } from "@/lib/api-error";
import { formatDateTime } from "@/lib/format";
import { useModules } from "@/auth/ModulesContext";
import { usePermissions } from "@/lib/permissions";
import { useIsDemo } from "@/demo/DemoContext";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { EmptyState, ErrorState, HeaderRow, LoadingState, TH, TableShell } from "@/components/PageUi";
import { cn } from "@/lib/utils";
import type { components } from "@dolfin/api-admin";

type AccountingConnectionResponse = components["schemas"]["AccountingConnectionResponse"];
type LedgerAccountResponse = components["schemas"]["LedgerAccountResponse"];
type AccountingTaxRateResponse = components["schemas"]["AccountingTaxRateResponse"];
type TrackingCategoryResponse = components["schemas"]["TrackingCategoryResponse"];

type TabValue = "accounts" | "tax-rates" | "tracking-categories";

// How invoices and bills post is chosen on each area's Accounting page. The synced tables are listed so a sync
// can be checked; Sync now follows the server's rate limit (canSyncNow) rather than guessing.
export function AccountingSettings() {
  const api = useDolfinClient();
  const { hasModule } = useModules();
  // Disconnecting is Owner or Admin; connecting and syncing are anyone's.
  const { can } = usePermissions();
  const canEdit = can("connectAccounting");
  // A demo organisation's fake connection is set up by its seeding and stays as it is (D7).
  const isDemo = useIsDemo();
  const [loadFailed, setLoadFailed] = useState(false);
  const [connection, setConnection] = useState<AccountingConnectionResponse | null>(null);
  const [accounts, setAccounts] = useState<LedgerAccountResponse[]>([]);
  const [addingAccount, setAddingAccount] = useState(false);
  const [taxRates, setTaxRates] = useState<AccountingTaxRateResponse[]>([]);
  const [categories, setCategories] = useState<TrackingCategoryResponse[]>([]);
  const [tab, setTab] = useState<TabValue>("accounts");
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 400);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const locked = busy || isDemo;
  const [confirmingDisconnect, setConfirmingDisconnect] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const [connectionResult, accountsResult, taxResult, categoryResult] = await Promise.all([
      api.GET("/v1/accounting/connection", {}),
      // Inactive included: an archived account is exactly what explains coding that suddenly stopped working.
      api.GET("/v1/accounting/ledger-accounts", { params: { query: { IncludeInactive: true } } }),
      api.GET("/v1/accounting/tax-rates", {}),
      api.GET("/v1/accounting/tracking-categories", {}),
    ]);
    setLoadFailed(Boolean(connectionResult.error));
    setConnection(connectionResult.data ?? null);
    setAccounts(accountsResult.data ?? []);
    setTaxRates(taxResult.data ?? []);
    setCategories(categoryResult.data ?? []);
    setLoading(false);
  }, [api]);

  useEffect(() => {
    load();
  }, [load]);

  async function connect() {
    setBusy(true);
    const { data, error } = await api.POST("/v1/accounting/connection-session", {
      body: { redirectUri: window.location.href },
    });
    if (error || !data) {
      toast.error(apiErrorMessage(error, "Couldn't start the connection."));
    } else if (data.url) {
      window.location.href = data.url;
      return;
    } else {
      toast.success("Connection session created.");
    }
    setBusy(false);
  }

  async function sync() {
    setBusy(true);
    const { error } = await api.POST("/v1/accounting/sync", {});
    if (error) {
      toast.error(apiErrorMessage(error, "Couldn't sync."));
    } else {
      toast.success("Synced your chart of accounts, tax rates and tracking categories.");
      await load();
    }
    setBusy(false);
  }

  async function disconnect() {
    setBusy(true);
    const { error } = await api.DELETE("/v1/accounting/connection", {});
    if (error) {
      toast.error(apiErrorMessage(error, "Couldn't disconnect."));
    } else {
      toast.success(`Disconnected from ${connection?.serviceDisplayName ?? "your accounting platform"}.`);
      await load();
    }
    setBusy(false);
  }

  const connected = connection?.status === "Callable";
  const platformLabel = connection?.serviceDisplayName || connection?.serviceId || "your accounting platform";
  const where = connection?.companyName ? `${connection.companyName} in ${platformLabel}` : platformLabel;
  const term = debouncedSearch.trim().toLowerCase();

  const filteredAccounts = term
    ? accounts.filter(
        (a) =>
          a.name?.toLowerCase().includes(term) ||
          a.displayId?.toLowerCase().includes(term) ||
          a.classification?.toLowerCase().includes(term),
      )
    : accounts;

  const filteredRates = term
    ? taxRates.filter(
        (t) => t.code?.toLowerCase().includes(term) || t.name?.toLowerCase().includes(term),
      )
    : taxRates;

  const filteredCategories = term
    ? categories.filter((c) => c.name?.toLowerCase().includes(term))
    : categories;

  if (loading) {
    return <LoadingState label="Loading your accounting connection..." />;
  }

  if (loadFailed) {
    return <ErrorState message="Couldn't load your accounting connection." onRetry={load} />;
  }

  if (!connected && connection?.status !== "Broken") {
    return (
      <EmptyState
          icon={Landmark}
          title={connection?.status === "Revoked" ? `Disconnected from ${where}` : "No accounting platform connected"}
          action={
            <Button size="sm" disabled={locked} onClick={connect}>
              <Link2 className="size-4" /> Connect accounting platform
            </Button>
          }
        >
          {connection?.status === "Revoked" ? (
            <>
              Nothing posts to your books while disconnected. What is already there stays, and the rest waits
              until you connect again. Connecting a different company replaces the accounts synced from this one.
            </>
          ) : (
            <>
              Connect Xero or QuickBooks once for the whole organisation. Invoices, bills and expenses then code
              to your own chart of accounts and post to your books without re-keying.
            </>
          )}
        </EmptyState>
    );
  }

  return (
    <div className="@container">
      <section className="rounded-xl border bg-card/60 p-5 backdrop-blur-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="grid size-10 place-items-center overflow-hidden rounded-xl border bg-muted/50">
              {connection?.serviceIconUrl ? (
                <img src={connection.serviceIconUrl} alt="" className="size-7 object-contain" />
              ) : (
                <Landmark className="size-5 text-muted-foreground" />
              )}
            </span>
            <div>
              {/* The company first: "QuickBooks" alone does not say which books Dolfin posts to. */}
              <div className="font-semibold">
                {connection?.companyName || connection?.serviceDisplayName || connection?.serviceId || "Accounting platform"}
              </div>
              <div className="flex flex-wrap items-center gap-x-2 text-xs">
                {connection?.companyName ? (
                  <span className="text-muted-foreground">
                    {connection.serviceDisplayName || connection.serviceId}
                  </span>
                ) : null}
                {connected ? (
                  <span className="inline-flex items-center gap-1.5 font-medium text-emerald-600">
                    <span className="size-1.5 rounded-full bg-emerald-500" /> Connected
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 font-medium text-destructive">
                    <span className="size-1.5 rounded-full bg-destructive" /> Needs reconnecting
                  </span>
                )}
              </div>
            </div>
          </div>

          {connected ? (
            <div className="flex flex-wrap items-center gap-3">
              <Button variant="outline" size="sm" disabled={locked} onClick={() => setAddingAccount(true)}>
                <Plus className="size-4" /> Add account
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={locked || connection?.canSyncNow === false}
                onClick={sync}
              >
                {busy ? <Spinner className="size-4" /> : <RefreshCw className="size-4" />} Sync now
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-destructive hover:text-destructive"
                disabled={locked || !canEdit}
                onClick={() => setConfirmingDisconnect(true)}
              >
                <Unplug className="size-4" /> Disconnect
              </Button>
            </div>
          ) : (
            <Button size="sm" disabled={locked} onClick={connect}>
              <Link2 className="size-4" /> Reconnect
            </Button>
          )}
        </div>

        <dl className="mt-5 grid grid-cols-2 gap-px overflow-hidden rounded-lg border bg-border @2xl:grid-cols-4">
          <Stat label="Accounts" value={connection?.ledgerAccountCount ?? 0} />
          <Stat label="Tax rates" value={connection?.taxRateCount ?? 0} />
          <Stat label="Tracking categories" value={connection?.trackingCategoryCount ?? 0} />
          <Stat
            label="Last synced"
            value={
              <>
                {connection?.lastSyncedAt ? formatDateTime(connection.lastSyncedAt) : "Never"}
                {/* Why Sync now is greyed out, beside the time it refers to. */}
                {connection?.canSyncNow === false && connection.retryAfterSeconds ? (
                  <span className="block text-xs font-normal text-muted-foreground">
                    Next sync in {Math.ceil(Number(connection.retryAfterSeconds) / 60)} min
                  </span>
                ) : null}
              </>
            }
            small
          />
        </dl>

        <CompanyDetails connection={connection} />

        {connection?.companyCurrency ? (
          <p className="mt-4 text-xs text-muted-foreground">
            Your books are in {connection.companyCurrency}. Invoices and bills in other currencies are not pushed.
          </p>
        ) : null}

        {connection?.lastSyncError ? (
          <p className="mt-4 flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-xs text-destructive">
            <TriangleAlert className="mt-px size-3.5 shrink-0" />
            {connection.lastSyncError}
          </p>
        ) : null}
      </section>

      {connected && accounts.length === 0 ? (
        <div className="mt-6">
          <EmptyState
            icon={Landmark}
            title="No accounts synced yet"
            action={
              <Button size="sm" disabled={locked || connection?.canSyncNow === false} onClick={sync}>
                {busy ? <Spinner className="size-4" /> : <RefreshCw className="size-4" />} Sync accounts
              </Button>
            }
          >
            Sync your chart of accounts and tax rates from {platformLabel} before choosing where things post.
          </EmptyState>
        </div>
      ) : connected ? (
        <>
          <div className="mt-6 mb-4 flex flex-wrap items-center justify-between gap-3">
            <Tabs value={tab} onValueChange={(v) => setTab(v as TabValue)}>
              <TabsList>
                <TabsTrigger value="accounts">Accounts ({accounts.length})</TabsTrigger>
                <TabsTrigger value="tax-rates">Tax rates ({taxRates.length})</TabsTrigger>
                <TabsTrigger value="tracking-categories">
                  Tracking categories ({categories.length})
                </TabsTrigger>
              </TabsList>
            </Tabs>
            <div className="relative w-full max-w-xs">
              <Search className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search"
                className="h-9 pl-8"
              />
            </div>
          </div>

          {tab === "accounts" ? (
            <AccountsTable accounts={filteredAccounts} />
          ) : tab === "tax-rates" ? (
            <TaxRatesTable rates={filteredRates} />
          ) : (
            <TrackingCategoriesTable categories={filteredCategories} />
          )}
        </>
      ) : null}

      {connected && (hasModule("ar.accounting") || hasModule("ap.accounting")) ? (
        <p className="mt-6 text-sm text-muted-foreground">
          Where{" "}
          {hasModule("ar.accounting") ? (
            <Link to="/ar/accounting" className="font-medium underline decoration-muted-foreground/40 underline-offset-4 transition-colors hover:decoration-current">
              invoices
            </Link>
          ) : null}
          {hasModule("ar.accounting") && hasModule("ap.accounting") ? " and " : null}
          {hasModule("ap.accounting") ? (
            <Link to="/ap/accounting" className="font-medium underline decoration-muted-foreground/40 underline-offset-4 transition-colors hover:decoration-current">
              bills
            </Link>
          ) : null}{" "}
          post is chosen under each area's Accounting page.
        </p>
      ) : null}

      <AlertDialog open={confirmingDisconnect} onOpenChange={setConfirmingDisconnect}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disconnect {where}?</AlertDialogTitle>
            <AlertDialogDescription>
              Dolfin stops posting invoices, bills and expenses to your books and gives up its access to{" "}
              {platformLabel}. What is already posted stays in your books; the rest waits until you connect
              again. Your synced accounts are kept for reference.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction variant="destructive" onClick={disconnect}>
              Disconnect
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <CreateLedgerAccountDialog
        open={addingAccount}
        onOpenChange={setAddingAccount}
        purpose="chart"
        platformName={connection?.serviceDisplayName ?? null}
        companyName={connection?.companyName ?? null}
        onCreated={() => load()}
      />
    </div>
  );
}

function Stat({ label, value, small }: { label: string; value: React.ReactNode; small?: boolean }) {
  return (
    <div className="bg-card px-4 py-3">
      <dt className="text-[10px] font-semibold tracking-[0.14em] text-muted-foreground uppercase">{label}</dt>
      <dd className={cn("mt-1 font-semibold tabular-nums", small ? "text-sm" : "text-2xl")}>{value}</dd>
    </div>
  );
}

// Account types arrive in the platform's own casing ("current_asset"). Readable is enough.
function humanise(value?: string | null) {
  if (!value) return "-";
  const words = value.replace(/[_-]+/g, " ").trim().toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function ActiveStatus({ active }: { active?: boolean }) {
  return active ? (
    <span className="inline-flex items-center gap-1.5 text-xs text-emerald-600">
      <span className="size-1.5 rounded-full bg-emerald-500" /> Active
    </span>
  ) : (
    // Retained rather than deleted, so historical coding still resolves - but it can no longer be posted
    // to, which is worth seeing.
    <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
      <span className="size-1.5 rounded-full bg-muted-foreground/60" /> Archived
    </span>
  );
}

function AccountsTable({ accounts }: { accounts: LedgerAccountResponse[] }) {
  if (accounts.length === 0) {
    return <NothingFound what="accounts" />;
  }

  return (
    <TableShell className="max-h-[26rem] overflow-y-auto">
      <Table>
        <TableHeader>
          <HeaderRow>
            <TableHead className={TH}>Code</TableHead>
            <TableHead className={TH}>Name</TableHead>
            <TableHead className={TH}>Classification</TableHead>
            <TableHead className={TH}>Type</TableHead>
            <TableHead className={TH}>Status</TableHead>
          </HeaderRow>
        </TableHeader>
        <TableBody>
          {accounts.map((account) => (
            <TableRow key={account.id}>
              <TableCell className="font-mono text-xs">{account.displayId || "-"}</TableCell>
              <TableCell className="font-semibold">
                {account.name}
                {account.parentAccountId ? (
                  <span className="block text-xs text-muted-foreground">Sub-account</span>
                ) : null}
              </TableCell>
              <TableCell>
                {account.classification ? (
                  <Badge variant="secondary">{humanise(account.classification)}</Badge>
                ) : (
                  <span className="text-muted-foreground">-</span>
                )}
              </TableCell>
              <TableCell className="text-muted-foreground">{humanise(account.accountType)}</TableCell>
              <TableCell>
                <ActiveStatus active={account.isActive} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableShell>
  );
}

function TaxRatesTable({ rates }: { rates: AccountingTaxRateResponse[] }) {
  if (rates.length === 0) {
    return <NothingFound what="tax rates" />;
  }

  return (
    <TableShell className="max-h-[26rem] overflow-y-auto">
      <Table>
        <TableHeader>
          <HeaderRow>
            <TableHead className={TH}>Code</TableHead>
            <TableHead className={TH}>Name</TableHead>
            <TableHead className={cn(TH, "text-right")}>Rate</TableHead>
            <TableHead className={TH}>Status</TableHead>
          </HeaderRow>
        </TableHeader>
        <TableBody>
          {rates.map((rate) => (
            <TableRow key={rate.id}>
              {/* The code, not the name, is what goes on a pushed expense line - so it leads. */}
              <TableCell className="font-mono text-xs">{rate.code}</TableCell>
              <TableCell className="font-semibold">{rate.name || "-"}</TableCell>
              <TableCell className="text-right tabular-nums">
                {rate.rate != null ? `${rate.rate}%` : "-"}
              </TableCell>
              <TableCell>
                <ActiveStatus active={rate.isActive} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableShell>
  );
}

function TrackingCategoriesTable({ categories }: { categories: TrackingCategoryResponse[] }) {
  if (categories.length === 0) {
    return (
      <EmptyState icon={Tags} title="No tracking categories">
        These are project, department and cost-centre dimensions. Dolfin syncs them ready for use, but does
        not assign them yet.
      </EmptyState>
    );
  }

  return (
    <TableShell className="max-h-[26rem] overflow-y-auto">
      <Table>
        <TableHeader>
          <HeaderRow>
            <TableHead className={TH}>Name</TableHead>
            <TableHead className={TH}>Status</TableHead>
          </HeaderRow>
        </TableHeader>
        <TableBody>
          {categories.map((category) => (
            <TableRow key={category.id}>
              <TableCell className="font-semibold">
                {category.name}
                {category.parentId ? (
                  <span className="block text-xs text-muted-foreground">Nested</span>
                ) : null}
              </TableCell>
              <TableCell>
                <ActiveStatus active={category.isActive} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableShell>
  );
}

function NothingFound({ what }: { what: string }) {
  return (
    <EmptyState icon={SearchX} title={`No ${what} found`}>
      Try syncing, or clear your search.
    </EmptyState>
  );
}

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

// What the platform says about the company the books belong to. Only what it told us is shown.
function CompanyDetails({ connection }: { connection: AccountingConnectionResponse | null | undefined }) {
  if (!connection) return null;
  const rows: [string, string][] = [];
  if (connection.companyLegalName && connection.companyLegalName !== connection.companyName)
    rows.push(["Legal name", connection.companyLegalName]);
  if (connection.companyCountry) rows.push(["Country", connection.companyCountry]);
  if (connection.companyCurrency) rows.push(["Base currency", connection.companyCurrency]);
  if (connection.financialYearStartMonth)
    rows.push(["Financial year starts", MONTHS[Number(connection.financialYearStartMonth) - 1] ?? "-"]);
  if (connection.companySalesTaxNumber) rows.push(["VAT number", connection.companySalesTaxNumber]);
  if (connection.accountingMethod)
    rows.push(["Accounting method", connection.accountingMethod.charAt(0).toUpperCase() + connection.accountingMethod.slice(1)]);
  if (connection.connectedAt) rows.push(["Connected since", formatDateTime(connection.connectedAt)]);
  if (connection.companyId) rows.push(["Company ID", connection.companyId]);
  if (rows.length === 0) return null;

  return (
    <dl className="mt-4 grid grid-cols-1 gap-x-6 gap-y-2 text-sm @md:grid-cols-2 @2xl:grid-cols-4">
      {rows.map(([label, value]) => (
        <div key={label} className="min-w-0">
          <dt className="text-xs text-muted-foreground">{label}</dt>
          <dd className="truncate font-medium" title={value}>{value}</dd>
        </div>
      ))}
    </dl>
  );
}
